import { useQuery } from '@tanstack/react-query'
import { ModuleHeader, SpinnerWithBackground, TabBar } from '@viapeople/components'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useModulePermission } from '../../hooks/useModulePermission'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import { filterTabsByRole } from '../../utils/tabFiltering'
import { trpc } from '../../utils/trpc'
import { usePecStore } from './store'

/**
 * @fileoverview Manage PEC Page
 *
 * Main page for creating/editing PEC cycles with tab navigation
 */

const tabs = [
	{ id: 'general', label: 'General' },
	{ id: 'population', label: 'Population' },
	{ id: 'configuration', label: 'Configuration' },
	{ id: 'announcement-email', label: 'Announcement Email' },
	{ id: 'reminder-email', label: 'Reminder Email' },
	{ id: 'launch', label: 'Launch' },
	{ id: 'results', label: 'Results' },
]

export const ManagePecPage = () => {
	const { pecId } = useParams<{ pecId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()

	// Check PEC module permission
	const { isLoading: isCheckingPermission, isAuthorized, role } = useModulePermission('pec')

	// Redirect unauthorized users
	useEffect(() => {
		if (!isCheckingPermission && !isAuthorized) {
			toast.showToast('You do not have permission to access the PEC module', 'error')
			navigate('/')
		}
	}, [isCheckingPermission, isAuthorized, navigate, toast])

	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.client.organizations.getById.queryOptions({ id: organizationId }),
		enabled: !!organizationId,
	})

	const { data: pecData, isLoading: isLoadingPec } = useQuery({
		...trpc.client.pec.getById.queryOptions({
			id: pecId as string,
			organizationId: organizationId,
		}),
		enabled: !!pecId && pecId !== 'create' && !!organizationId,
	})

	const location = useLocation()
	const pathArray = location.pathname.split('/')
	const currentTab = pathArray?.[3] ?? 'general'

	const { pecCycle, setPecCycle } = usePecStore()

	useEffect(() => {
		if (pecData) {
			setPecCycle({
				id: pecData.id,
				name: pecData.name,
				description: pecData.description,
				status: pecData.status,
				organizationId: pecData.organizationId,
				populationId: pecData.populationId,
				promptText: pecData.promptText,
				ratingScale: pecData.ratingScale,
				ratingConfig: pecData.ratingConfig ?? null,
				allowNa: pecData.allowNa ?? false,
				textFeedbackEnabled: pecData.textFeedbackEnabled ?? true,
				emailTemplateId: pecData.emailTemplateId,
				reminderTemplateId: pecData.reminderTemplateId,
				reminderDays: pecData.reminderDays ?? 0,
				reminderLogic: pecData.reminderLogic,
				reminderMaxEmails: pecData.reminderMaxEmails ?? 0,
				launchedAt: pecData.launchedAt ? new Date(pecData.launchedAt) : null,
				closedAt: pecData.closedAt ? new Date(pecData.closedAt) : null,
				createdAt: new Date(pecData.createdAt),
				updatedAt: new Date(pecData.updatedAt),
			})
		} else if (organizationId) {
			setPecCycle({
				id: '',
				name: '',
				description: null,
				status: 'draft',
				organizationId,
				populationId: null,
				promptText: 'On a scale of 1-5, how well does this person demonstrate the following:',
				ratingScale: 5,
				ratingConfig: null,
				allowNa: false,
				textFeedbackEnabled: true,
				emailTemplateId: null,
				reminderTemplateId: null,
				reminderDays: 0,
				reminderLogic: null,
				reminderMaxEmails: 0,
				launchedAt: null,
				closedAt: null,
				createdAt: new Date(),
				updatedAt: new Date(),
			})
		}
	}, [pecData, organizationId, setPecCycle])

	const handleTabChange = (tabId: string) => {
		const pecPath = pecId === 'create' || !pecId ? 'create' : pecId
		navigate(`/pec/${pecPath}/${tabId}`)
	}

	const availableTabs = useMemo(() => filterTabsByRole(tabs, pecId, role), [pecId, role])

	// Show spinner while checking permissions or loading data
	if (isCheckingPermission || isLoadingOrg || isLoadingPec) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	// Don't render if not authorized (redirect will happen via useEffect)
	if (!isAuthorized) {
		return null
	}

	return (
		<main className='w-full p-6 min-h-full'>
			<div className='gap-6 flex flex-col'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Home', to: '/' },
						{ label: 'PEC', to: '/pec' },
						{
							label: pecCycle?.name || (pecId && pecId !== 'create' ? 'Manage PEC Cycle' : 'Create PEC Cycle'),
						},
					]}
					title={pecCycle?.name || (pecId && pecId !== 'create' ? 'Manage PEC Cycle' : 'Create PEC Cycle')}
				/>
				<TabBar tabs={availableTabs} activeTab={currentTab} onChange={handleTabChange} />
			</div>
			<Outlet context={{ organization, pecCycle }} />
		</main>
	)
}
